import { z } from "zod";
import { EntryShared, ParentPayload } from "@typedefs/modulesInterfaces";
import { entrySharedSchema, nullableNumber } from "@typedefs/zodSchemas";

/** Zod schema for validating storage entry API responses - extends EntryShared */
export const storageEntryResponseSchema = entrySharedSchema.extend({
		activity: z.number().optional(),
		fuel_type_start: nullableNumber.optional(),
		fuel_type_w: nullableNumber.optional(),
		fuel_type_wo: nullableNumber.optional(),
		fuel_type_thread: nullableNumber.optional(),
		quantity_consumed_per_year_start: nullableNumber.optional(),
		quantity_consumed_per_year_w: nullableNumber.optional(),
		quantity_consumed_per_year_wo: nullableNumber.optional(),
		quantity_consumed_per_year_thread: nullableNumber.optional(),
		is_refrigerant_used: z.boolean().optional(),
		refrigerant_type_start: nullableNumber.optional(),
		refrigerant_type_w: nullableNumber.optional(),
		refrigerant_type_wo: nullableNumber.optional(),
		refrigerant_type_thread: nullableNumber.optional(),
		total_refrigerant_leakage_start: nullableNumber.optional(),
		total_refrigerant_leakage_w: nullableNumber.optional(),
		total_refrigerant_leakage_wo: nullableNumber.optional(),
		total_refrigerant_leakage_thread: nullableNumber.optional(),
		electricity_ef_t2_start: nullableNumber.optional(),
		electricity_ef_t2_w: nullableNumber.optional(),
		electricity_ef_t2_wo: nullableNumber.optional(),
		country_t2: nullableNumber.optional(),
		ef_source: z.number().optional(),
		emission_factor_t2_start: nullableNumber.optional(),
		emission_factor_t2_w: nullableNumber.optional(),
		emission_factor_t2_wo: nullableNumber.optional(),
		energy_ef_co2_t2_start: nullableNumber.optional(),
		energy_ef_co2_t2_w: nullableNumber.optional(),
		energy_ef_co2_t2_wo: nullableNumber.optional(),
		energy_ef_ch4_t2_start: nullableNumber.optional(),
		energy_ef_ch4_t2_w: nullableNumber.optional(),
		energy_ef_ch4_t2_wo: nullableNumber.optional(),
		energy_ef_n2o_t2_start: nullableNumber.optional(),
		energy_ef_n2o_t2_w: nullableNumber.optional(),
		energy_ef_n2o_t2_wo: nullableNumber.optional(),
		transmission_loss_t2_start: nullableNumber.optional(),
		transmission_loss_t2_w: nullableNumber.optional(),
		transmission_loss_t2_wo: nullableNumber.optional(),
		// excluded from form — display-only / computed
		mwh_renewables_start: nullableNumber.optional(),
		mwh_renewables_w: nullableNumber.optional(),
		mwh_renewables_wo: nullableNumber.optional(),
		account_for_co2: z.boolean().nullable().optional(),
		name: z.string().nullable().optional(),
	});

/** Inferred type from storageEntryResponseSchema - use for validated API responses */
// export type StorageEntryResponseInferred = z.infer<typeof storageEntryResponseSchema>;

export interface StorageEntryResponse extends EntryShared {
  fuel_type_start: number | null,
  fuel_type_w: number | null,
  fuel_type_wo: number | null,
  fuel_type_thread: number | null, //TODO. check it exists

  quantity_consumed_per_year_start: number | null,
  quantity_consumed_per_year_w: number | null,
  quantity_consumed_per_year_wo: number | null,
  quantity_consumed_per_year_thread: number | null, //TODO. check it exists

  is_refrigerant_used: boolean,

  refrigerant_type_start: number | null,
  refrigerant_type_w: number | null,
  refrigerant_type_wo: number | null,
  refrigerant_type_thread: number | null,

  total_refrigerant_leakage_start: number | null,
  total_refrigerant_leakage_w: number | null,
  total_refrigerant_leakage_wo: number | null,
  total_refrigerant_leakage_thread: number | null,

  //T2
  electricity_ef_t2_start: number | null,
  electricity_ef_t2_w: number | null,
  electricity_ef_t2_wo: number | null,
  country_t2: number | null,
  ef_source: number,
  emission_factor_t2_start: number | null,
  emission_factor_t2_w: number | null,
  emission_factor_t2_wo: number | null,
  energy_ef_co2_t2_start: number | null,
	energy_ef_co2_t2_w: number | null,
	energy_ef_co2_t2_wo: number | null,
	energy_ef_ch4_t2_start: number | null,
	energy_ef_ch4_t2_w: number | null,
	energy_ef_ch4_t2_wo: number | null,
	energy_ef_n2o_t2_start: number | null,
	energy_ef_n2o_t2_w: number | null,
	energy_ef_n2o_t2_wo: number | null,
  transmission_loss_t2_start: number | null,
  transmission_loss_t2_w: number | null,
  transmission_loss_t2_wo: number | null,

  // excluded from form — display-only / computed
  mwh_renewables_start: number | null,
  mwh_renewables_w: number | null,
  mwh_renewables_wo: number | null,
  account_for_co2: boolean | null,
  name: string | null,
}

export interface StorageEntryDefaults {
  electricity_ef_t2_start_default: number | null,
  electricity_ef_t2_w_default: number | null,
  electricity_ef_t2_wo_default: number | null,
  country_t2_default: number | null,
  ef_source_default: number,
  emission_factor_t2_start_default: number | null,
  emission_factor_t2_w_default: number | null,
  emission_factor_t2_wo_default: number | null,
  energy_ef_co2_t2_start_default: number | null,
	energy_ef_co2_t2_w_default: number | null,
	energy_ef_co2_t2_wo_default: number | null,
	energy_ef_ch4_t2_start_default: number | null,
	energy_ef_ch4_t2_w_default: number | null,
	energy_ef_ch4_t2_wo_default: number | null,
	energy_ef_n2o_t2_start_default: number | null,
	energy_ef_n2o_t2_w_default: number | null,
	energy_ef_n2o_t2_wo_default: number | null,
  transmission_loss_t2_start_default: number | null,
  transmission_loss_t2_w_default: number | null,
  transmission_loss_t2_wo_default: number | null,
}

export interface StorageEntryData extends StorageEntryResponse, Partial<StorageEntryDefaults> { }

export interface ModuleFull {
  storages: StorageEntryData[],
  parent: ParentPayload
}